import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2, Pencil, X } from 'lucide-react';

import { loadPatient, updatePatient } from './api';
import { tx } from './i18n';
import { codeLabel } from './labels';
import { XomicsSelect } from './parts';
import { errorMessage } from './ui';

// Edits the details of the patient whose record is open: their name, how to reach
// them, their date of birth and sex. Their personal ID is not edited. The form is
// filled from what is stored and sends only what changed; what a sex may be
// arrives with the patient. Drawn with the Mobile app section's dialog parts,
// over the record.

const FIELDS = ['name', 'email', 'phone', 'dateOfBirth', 'gender'];

function formOf(stored) {
  return {
    name: stored.name || '',
    email: stored.email || '',
    phone: stored.phone || '',
    dateOfBirth: stored.dateOfBirth || '',
    gender: stored.gender || '',
  };
}

// Only what differs from what is stored. An emptied field clears the stored value.
function changesOf(stored, form) {
  const before = formOf(stored);
  const changes = {};
  FIELDS.forEach((field) => {
    if (form[field] === before[field]) return;
    const value = form[field].trim();
    changes[field] = value === '' ? null : value;
  });
  return changes;
}

// The server takes a date of birth up to today in UTC.
function today() {
  return new Date().toISOString().slice(0, 10);
}

export function PatientEditDialog({ patient, onClose, onSaved }) {
  const [stored, setStored] = useState(null);
  const [genders, setGenders] = useState([]);
  const [form, setForm] = useState(null);
  const [readError, setReadError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const set = (field) => (event) => setForm((previous) => ({ ...previous, [field]: event.target.value }));

  useEffect(() => {
    const close = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);

  // Nothing is typed over a patient that could not be read: the form appears
  // only once what is stored has arrived.
  useEffect(() => {
    let live = true;
    setReadError(null);
    loadPatient(patient.id)
      .then((loaded) => {
        if (!live) return;
        setStored(loaded.patient);
        setGenders(loaded.genders);
        setForm(formOf(loaded.patient));
      })
      .catch((caught) => { if (live) setReadError(errorMessage(caught)); });
    return () => { live = false; };
  }, [patient.id, attempt]);

  const changes = stored === null ? {} : changesOf(stored, form);
  const changed = Object.keys(changes).length > 0;

  async function save(event) {
    event.preventDefault();
    if (!changed) return;
    setSaving(true);
    setSaveError(null);
    try {
      onSaved(await updatePatient(patient.id, changes));
      onClose();
    } catch (caught) {
      setSaveError(errorMessage(caught));
      setSaving(false);
    }
  }

  const subProject = typeof patient.subProject === 'string' ? patient.subProject : patient.subProject?.code;
  return (
    <div className="xo-mob-dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <motion.div className="xo-mob-dialog is-wide" role="dialog" aria-modal="true" aria-labelledby="xo-rec-edit-title"
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.18, ease: 'easeOut' }}>
        <header>
          <span className="xo-mob-dialog-icon"><Pencil aria-hidden="true" /></span>
          <div>
            <h2 id="xo-rec-edit-title">{tx('ui.patient.edit_title')}</h2>
            <p>{[tx('ui.patient.patient_n', { id: patient.id }), subProject || tx('ui.patient.no_subproject')].join(' · ')}</p>
          </div>
          <button type="button" className="xo-mob-icon-button" aria-label={tx('ui.mobile.close')} onClick={onClose}><X /></button>
        </header>
        {readError !== null && (
          <div className="xo-mob-form">
            <p className="xo-alert t-warn xo-rec-link-alert" role="alert">
              {readError}
              <span><button type="button" className="xo-link" onClick={() => setAttempt((count) => count + 1)}>{tx('ui.patient.try_again')}</button></span>
            </p>
            <div className="xo-mob-dialog-actions">
              <button type="button" className="xo-btn-2" onClick={onClose}>{tx('ui.mobile.close')}</button>
            </div>
          </div>
        )}
        {readError === null && form === null && (
          <div className="xo-mob-form" role="status" aria-live="polite">
            <small className="is-muted"><Loader2 className="xo-spin" aria-hidden="true" /> {tx('ui.patient.loading_record')}</small>
          </div>
        )}
        {readError === null && form !== null && (
          <form onSubmit={save} className="xo-mob-form">
            <label><span>{tx('ui.patient.edit_name')}</span>
              <input value={form.name} onChange={set('name')} autoComplete="off" maxLength={255} required autoFocus />
            </label>
            <div className="xo-mob-form-row">
              <label><span>{tx('ui.mobile.email')}</span>
                <input type="email" value={form.email} onChange={set('email')} autoComplete="off" maxLength={255} />
              </label>
              <label><span>{tx('ui.mobile.phone')}</span>
                <input type="tel" value={form.phone} onChange={set('phone')} autoComplete="off" maxLength={255} />
              </label>
            </div>
            <div className="xo-mob-form-row">
              <label><span>{tx('ui.mobile.date_of_birth')}</span>
                <input type="date" value={form.dateOfBirth} onChange={set('dateOfBirth')} min="1900-01-01" max={today()} />
              </label>
              <label><span>{tx('ui.patient.sex')}</span>
                <XomicsSelect value={form.gender} onChange={set('gender')} aria-label={tx('ui.patient.sex')}>
                  <option value="">{tx('ui.patient.not_recorded')}</option>
                  {genders.map((code) => <option key={code} value={code}>{codeLabel('gender', code)}</option>)}
                </XomicsSelect>
              </label>
            </div>
            {saveError !== null && <p className="xo-alert t-warn" role="alert">{saveError}</p>}
            <div className="xo-mob-dialog-actions">
              <button type="button" className="xo-btn-2" onClick={onClose}>{tx('ui.mobile.cancel')}</button>
              <button type="submit" className="xo-btn" disabled={!changed || saving}>
                {saving && <Loader2 className="xo-spin" aria-hidden="true" />}{tx('ui.patient.edit_save')}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
}
